import React, { useState, useEffect, useRef } from "react";
import dayjs from "dayjs";
import DeleteConfirmationModal from "../../trainer/DeleteConfirmationModal";
import { getServiceDuration } from '../../../utils/calendar/durationService';
import "../../../styles/MobileOwnerCalendar.css";

const dayNamesPl = ["Pon", "Wt", "Śr", "Czw", "Pt", "Sob", "Nie"];

const MobileOwnerCalendar = ({
                                 currentWeekStart,
                                 selectedTrainer,
                                 openingHours,
                                 trainings,
                                 trainingLocks,
                                 confirmedTrainings,
                                 slotBookings,
                                 schoolSettings,
                                 selectedDogId,
                                 selectedServiceId,
                                 selectedService,
                                 selectedSlots,
                                 onPrevWeek,
                                 onNextWeek,
                                 onBookSlot,
                                 onToggleSlot,
                                 onDeclineTraining
                             }) => {
    const [selectedMobileDay, setSelectedMobileDay] = useState(dayjs());
    const [swipeOffset, setSwipeOffset] = useState(0);
    const [isSwipeAnimating, setIsSwipeAnimating] = useState(false);
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [trainingToDelete, setTrainingToDelete] = useState(null);

    const daysGridRef = useRef(null);

    // Get service duration - default to 60 minutes if not specified
    const serviceDurationMinutes = selectedService ? getServiceDuration(selectedService) : 60;

    const dayOfWeekMapping = {
        1: "MONDAY", 2: "TUESDAY", 3: "WEDNESDAY", 4: "THURSDAY",
        5: "FRIDAY", 6: "SATURDAY", 7: "SUNDAY"
    };

    // Initialize selected mobile day to current day if within current week
    useEffect(() => {
        const today = dayjs();
        const weekStart = currentWeekStart;
        const weekEnd = currentWeekStart.add(6, 'day');

        if (today.isBetween(weekStart, weekEnd, 'day', '[]')) {
            setSelectedMobileDay(today);
        } else {
            setSelectedMobileDay(weekStart);
        }
    }, [currentWeekStart]);

    // Swipe handling for the days grid area
    useEffect(() => {
        const daysGrid = daysGridRef.current;
        if (!daysGrid) return;

        let startX = null;
        let startY = null;
        let isHorizontal = null;

        const handleStart = (e) => {
            if (isSwipeAnimating) return;
            const touch = e.touches[0];
            startX = touch.clientX;
            startY = touch.clientY;
            isHorizontal = null;
        };

        const handleMove = (e) => {
            if (!startX || isSwipeAnimating) return;
            const touch = e.touches[0];
            const deltaX = touch.clientX - startX;
            const deltaY = touch.clientY - startY;

            if (isHorizontal === null && (Math.abs(deltaX) > 5 || Math.abs(deltaY) > 5)) {
                isHorizontal = Math.abs(deltaX) > Math.abs(deltaY);
            }

            if (isHorizontal === true) {
                e.preventDefault();
                const constrainedDelta = Math.max(-100, Math.min(100, deltaX));
                setSwipeOffset(constrainedDelta);
            }
        };

        const handleEnd = (e) => {
            if (!startX || isHorizontal !== true || isSwipeAnimating) {
                startX = null;
                startY = null;
                isHorizontal = null;
                setSwipeOffset(0);
                return;
            }

            const touch = e.changedTouches[0];
            const deltaX = touch.clientX - startX;
            const threshold = 50;

            setIsSwipeAnimating(true);

            if (Math.abs(deltaX) > threshold) {
                if (deltaX > 0) {
                    onPrevWeek();
                } else {
                    onNextWeek();
                }
            }

            setTimeout(() => {
                setSwipeOffset(0);
                setIsSwipeAnimating(false);
                startX = null;
                startY = null;
                isHorizontal = null;
            }, 200);
        };

        daysGrid.addEventListener('touchstart', handleStart, { passive: true });
        daysGrid.addEventListener('touchmove', handleMove, { passive: false });
        daysGrid.addEventListener('touchend', handleEnd, { passive: true });
        daysGrid.addEventListener('touchcancel', handleEnd, { passive: true });

        return () => {
            daysGrid.removeEventListener('touchstart', handleStart);
            daysGrid.removeEventListener('touchmove', handleMove);
            daysGrid.removeEventListener('touchend', handleEnd);
            daysGrid.removeEventListener('touchcancel', handleEnd);
        };
    }, [isSwipeAnimating, onPrevWeek, onNextWeek]);

    const handleDeclineTrainingConfirm = (training) => {
        setTrainingToDelete(training);
        setDeleteModalOpen(true);
    };

    const confirmDeleteTraining = () => {
        if (!trainingToDelete) return;
        onDeclineTraining(trainingToDelete);
        setDeleteModalOpen(false);
        setTrainingToDelete(null);
    };

    const cancelDeleteTraining = () => {
        setDeleteModalOpen(false);
        setTrainingToDelete(null);
    };

    const parseTime = (timeStr, dayDate) => {
        const [hour, minute] = timeStr.split(":").map(Number);
        return dayDate.clone().hour(hour).minute(minute).second(0);
    };

    const daysOfWeek = Array.from({ length: 7 }, (_, i) => currentWeekStart.add(i, "day"));

    // Check if a specific slot is booked by the owner
    const isSlotBooked = (slotTime) => {
        return trainings.find(training => {
            const trainingStart = dayjs(training.startTime);
            const trainingEnd = dayjs(training.endTime);
            const slotEnd = slotTime.add(serviceDurationMinutes, 'minute');

            // Check for overlap
            return slotTime.isBefore(trainingEnd) && slotEnd.isAfter(trainingStart);
        });
    };

    // Check if a slot is locked
    const isSlotLocked = (slotTime) => {
        const slotEnd = slotTime.add(serviceDurationMinutes, 'minute');
        return trainingLocks.some(lock => {
            const lockStart = dayjs(lock.startTime);
            const lockEnd = dayjs(lock.endTime);
            return slotTime.isBefore(lockEnd) && slotEnd.isAfter(lockStart);
        });
    };

    // Check if a slot is confirmed by others
    const isSlotConfirmed = (slotTime) => {
        const slotEnd = slotTime.add(serviceDurationMinutes, 'minute');
        return confirmedTrainings.some(ct => {
            const ctStart = dayjs(ct.startTime);
            const ctEnd = dayjs(ct.endTime);
            return slotTime.isBefore(ctEnd) && slotEnd.isAfter(ctStart);
        });
    };

    const dayHasEvents = (dayDate) => {
        const dayKey = dayOfWeekMapping[dayDate.isoWeekday()];
        const oh = openingHours.find(h => h.dayOfWeek === dayKey);
        if (!oh) return false;

        // Check if there are any trainings, locks, or confirmed trainings on this day
        const dayStart = dayDate.startOf('day');
        const dayEnd = dayDate.endOf('day');

        const hasTraining = trainings.some(t => {
            const start = dayjs(t.startTime);
            return start.isSameOrAfter(dayStart) && start.isSameOrBefore(dayEnd);
        });

        const hasLock = trainingLocks.some(l => {
            const start = dayjs(l.startTime);
            return start.isSameOrAfter(dayStart) && start.isSameOrBefore(dayEnd);
        });

        const hasConfirmed = confirmedTrainings.some(ct => {
            const start = dayjs(ct.startTime);
            return start.isSameOrAfter(dayStart) && start.isSameOrBefore(dayEnd);
        });

        return hasTraining || hasLock || hasConfirmed;
    };

    // Get time slots for a day based on service duration
    const getTimeSlotsForDay = (dayDate) => {
        const dayKey = dayOfWeekMapping[dayDate.isoWeekday()];
        const oh = openingHours.find(h => h.dayOfWeek === dayKey);
        if (!oh) return [];

        const slots = [];
        const processedTimes = new Set(); // Track processed times to avoid duplicates

        // First, add all existing bookings with their actual durations
        const dayStart = dayDate.startOf('day');
        const dayEnd = dayDate.endOf('day');

        // Add owner's trainings
        trainings.forEach(training => {
            const trainingStart = dayjs(training.startTime);
            const trainingEnd = dayjs(training.endTime);

            if (trainingStart.isSameOrAfter(dayStart) && trainingStart.isSameOrBefore(dayEnd)) {
                const trainingDuration = trainingEnd.diff(trainingStart, 'minute');

                // Check cancellation policy
                const canCancel = !training.confirmed ||
                    (!schoolSettings?.minDaysBeforeCancel ||
                        trainingStart.startOf('day').isAfter(
                            dayjs().add(schoolSettings.minDaysBeforeCancel, 'day').startOf('day')
                        ));

                slots.push({
                    time: trainingStart,
                    endTime: trainingEnd,
                    type: training.confirmed ? 'confirmed' : 'pending',
                    bookedTraining: training,
                    bookingInfo: null,
                    isUnavailable: false,
                    canCancel,
                    actualDuration: trainingDuration
                });

                // Mark time range as processed
                let t = trainingStart.clone();
                while (t.isBefore(trainingEnd)) {
                    processedTimes.add(t.format('HH:mm'));
                    t = t.add(15, 'minute'); // Use 15-minute increments for tracking
                }
            }
        });

        // Now generate available slots with selected service duration
        let slotTime = parseTime(oh.openingTime, dayDate.clone());
        const closingTime = parseTime(oh.closingTime, dayDate.clone());
        const now = dayjs();

        // Important: Only create slots where the END time is within working hours
        while (slotTime.isBefore(closingTime)) {
            const slotEnd = slotTime.add(serviceDurationMinutes, 'minute');

            // Skip if slot would end after closing time
            if (slotEnd.isAfter(closingTime)) {
                break;
            }

            // Skip if this time is already covered by an existing booking
            const slotTimeStr = slotTime.format('HH:mm');
            if (processedTimes.has(slotTimeStr)) {
                slotTime = slotTime.add(15, 'minute'); // Move in small increments to find next free slot
                continue;
            }

            // Check if this slot would overlap with any existing booking
            let hasOverlap = false;

            for (const training of trainings) {
                const trainingStart = dayjs(training.startTime);
                const trainingEnd = dayjs(training.endTime);

                if (trainingStart.isSame(dayDate, 'day') &&
                    slotTime.isBefore(trainingEnd) && slotEnd.isAfter(trainingStart)) {
                    hasOverlap = true;
                    break;
                }
            }

            if (hasOverlap) {
                slotTime = slotTime.add(15, 'minute');
                continue;
            }

            // Check other blocking conditions
            const isLocked = isSlotLocked(slotTime);
            const isConfirmed = isSlotConfirmed(slotTime);
            const bookingInfo = slotBookings.find(b => dayjs(b.slot).isSame(slotTime, 'minute'));

            // Check booking constraints
            const bookingAllowed = !schoolSettings || slotTime.startOf('day').isAfter(
                dayjs().add(schoolSettings.minBookingDaysInAdvance || 0, 'day').startOf('day')
            );

            const isPast = slotTime.isBefore(now);
            const isUnavailable = isPast || !bookingAllowed;

            // Check month restrictions
            let monthBlocked = false;
            if (schoolSettings?.unlockedMonths && schoolSettings.unlockedMonths.length > 0) {
                const monthNames = [
                    'JANUARY', 'FEBRUARY', 'MARCH', 'APRIL', 'MAY', 'JUNE',
                    'JULY', 'AUGUST', 'SEPTEMBER', 'OCTOBER', 'NOVEMBER', 'DECEMBER'
                ];
                const slotMonth = monthNames[slotTime.month()];
                monthBlocked = !schoolSettings.unlockedMonths.includes(slotMonth);
            }

            let slotType = 'free';
            if (isLocked) {
                slotType = 'locked';
            } else if (isConfirmed) {
                slotType = 'confirmed-other';
            } else if (monthBlocked || isUnavailable) {
                slotType = 'unavailable';
            }

            slots.push({
                time: slotTime.clone(),
                endTime: slotEnd,
                type: slotType,
                bookedTraining: null,
                bookingInfo,
                isUnavailable: monthBlocked || isUnavailable,
                canCancel: false,
                actualDuration: serviceDurationMinutes
            });

            slotTime = slotTime.add(serviceDurationMinutes, 'minute');
        }

        // Sort slots by time
        slots.sort((a, b) => a.time.diff(b.time));

        return slots;
    };

    // Calculate time range for the day
    const getTimeRange = (dayDate) => {
        const dayKey = dayOfWeekMapping[dayDate.isoWeekday()];
        const oh = openingHours.find(h => h.dayOfWeek === dayKey);
        if (!oh) return null;

        const [startHour, startMinute] = oh.openingTime.split(':').map(Number);
        const [endHour, endMinute] = oh.closingTime.split(':').map(Number);

        const totalMinutes = (endHour * 60 + endMinute) - (startHour * 60 + startMinute);
        const totalHours = totalMinutes / 60;

        return {
            startHour,
            startMinute,
            endHour,
            endMinute,
            totalHours,
            totalMinutes
        };
    };

    // Mobile Week Overview Component
    const renderMobileWeekOverview = () => (
        <div className="mobile-week-overview">
            <div
                ref={daysGridRef}
                className={`mobile-days-grid ${isSwipeAnimating ? 'animating' : ''}`}
                style={{
                    transform: `translateX(${swipeOffset}px)`,
                    transition: isSwipeAnimating ? 'transform 0.2s ease-out' : 'none',
                    touchAction: 'pan-y'
                }}
            >
                {daysOfWeek.map((dayDate) => {
                    const isSelected = selectedMobileDay.isSame(dayDate, 'day');
                    const isToday = dayDate.isSame(dayjs(), 'day');
                    const hasEvents = dayHasEvents(dayDate);

                    return (
                        <div
                            key={dayDate.toString()}
                            className={`mobile-day-item ${isSelected ? 'selected' : ''} ${isToday ? 'today' : ''}`}
                            onClick={() => setSelectedMobileDay(dayDate)}
                        >
                            <div className="mobile-day-name">
                                {dayNamesPl[dayDate.isoWeekday() - 1]}
                            </div>
                            <div className="mobile-day-date">
                                {dayDate.format('DD')}
                            </div>
                            <div className={`mobile-day-indicator ${hasEvents ? 'has-events' : ''}`}></div>
                        </div>
                    );
                })}
            </div>
        </div>
    );

    // Mobile Day View Component - Desktop-like slot display with fixed hour grid
    const renderMobileDayView = () => {
        const timeSlots = getTimeSlotsForDay(selectedMobileDay);
        const dayKey = dayOfWeekMapping[selectedMobileDay.isoWeekday()];
        const oh = openingHours.find(h => h.dayOfWeek === dayKey);
        const timeRange = getTimeRange(selectedMobileDay);

        // Check if this month is blocked
        if (schoolSettings?.unlockedMonths) {
            const monthNames = [
                'JANUARY', 'FEBRUARY', 'MARCH', 'APRIL', 'MAY', 'JUNE',
                'JULY', 'AUGUST', 'SEPTEMBER', 'OCTOBER', 'NOVEMBER', 'DECEMBER'
            ];
            const dayMonth = monthNames[selectedMobileDay.month()];
            if (!schoolSettings.unlockedMonths.includes(dayMonth)) {
                return (
                    <div className="mobile-day-view">
                        <div className="mobile-day-header">
                            <h2 className="mobile-day-title">
                                {selectedMobileDay.format('dddd, DD MMMM YYYY')}
                            </h2>
                        </div>
                        <div className="mobile-no-working-hours">
                            <div className="mobile-no-events-icon">🚫</div>
                            <div className="mobile-no-events-text">Rezerwacje na ten miesiąc są zablokowane</div>
                        </div>
                    </div>
                );
            }
        }

        // Generate hour labels for the grid
        const generateHourLabels = () => {
            if (!timeRange) return [];
            const hours = [];
            // Only show hours where work actually happens
            // If work ends at 22:00, last hour shown should be 21:00
            for (let hour = timeRange.startHour; hour < timeRange.endHour; hour++) {
                hours.push(hour);
            }
            return hours;
        };

        const hourLabels = generateHourLabels();
        const hourHeight = 80; // Fixed height per hour in pixels

        // Group consecutive unavailable slots
        const groupSlots = () => {
            const grouped = [];
            let currentGroup = null;

            timeSlots.forEach((slot) => {
                const { type, bookedTraining, actualDuration } = slot;
                const shouldMerge = (type === 'unavailable' || type === 'locked' || type === 'confirmed-other') && !bookedTraining;

                if (shouldMerge) {
                    if (!currentGroup || currentGroup.type !== 'unavailable-merged') {
                        currentGroup = {
                            type: 'unavailable-merged',
                            startTime: slot.time,
                            endTime: slot.endTime,
                            slots: [slot]
                        };
                        grouped.push(currentGroup);
                    } else {
                        // Check if consecutive
                        if (slot.time.isSame(currentGroup.endTime, 'minute')) {
                            currentGroup.endTime = slot.endTime;
                            currentGroup.slots.push(slot);
                        } else {
                            currentGroup = {
                                type: 'unavailable-merged',
                                startTime: slot.time,
                                endTime: slot.endTime,
                                slots: [slot]
                            };
                            grouped.push(currentGroup);
                        }
                    }
                } else {
                    currentGroup = null;
                    grouped.push({
                        type: 'single',
                        ...slot,
                        actualDuration: actualDuration // Ensure actualDuration is passed through
                    });
                }
            });

            return grouped;
        };

        const groupedSlots = groupSlots();

        return (
            <div className="mobile-day-view">
                <div className="mobile-day-header">
                    <h2 className="mobile-day-title">
                        {selectedMobileDay.format('dddd, DD MMMM YYYY')}
                    </h2>
                    {oh && (
                        <div className="mobile-working-hours">
                            Godziny pracy: {oh.openingTime} - {oh.closingTime}
                        </div>
                    )}
                    {selectedService && (
                        <div className="mobile-service-info">
                            Czas trwania: {serviceDurationMinutes} min
                        </div>
                    )}
                </div>

                {!selectedTrainer ? (
                    <div className="mobile-no-working-hours">
                        <div className="mobile-no-events-icon">👤</div>
                        <div className="mobile-no-events-text">Wybierz trenera</div>
                    </div>
                ) : !oh ? (
                    <div className="mobile-no-working-hours">
                        <div className="mobile-no-events-icon">📅</div>
                        <div className="mobile-no-events-text">Brak godzin pracy w tym dniu</div>
                    </div>
                ) : !timeRange ? (
                    <div className="mobile-no-working-hours">
                        <div className="mobile-no-events-icon">✨</div>
                        <div className="mobile-no-events-text">Brak dostępnych terminów</div>
                    </div>
                ) : (
                    <div className="mobile-slots-container">
                        <div className="mobile-time-grid-container">
                            {/* Hour labels column */}
                            <div className="mobile-hour-labels">
                                {hourLabels.map((hour) => (
                                    <div
                                        key={hour}
                                        className="mobile-hour-label"
                                        style={{ height: `${hourHeight}px` }}
                                    >
                                        {`${hour.toString().padStart(2, '0')}:00`}
                                    </div>
                                ))}
                            </div>

                            {/* Slots column with relative positioning */}
                            <div
                                className="mobile-slots-column"
                                style={{
                                    height: `${hourLabels.length * hourHeight}px`,
                                    position: 'relative'
                                }}
                            >
                                {/* Hour grid lines */}
                                {hourLabels.map((hour, index) => (
                                    <div
                                        key={`line-${hour}`}
                                        className="mobile-hour-line"
                                        style={{
                                            top: `${index * hourHeight}px`,
                                            height: `${hourHeight}px`
                                        }}
                                    />
                                ))}

                                {/* Render grouped slots */}
                                {groupedSlots.map((item, groupIndex) => {
                                    if (item.type === 'unavailable-merged') {
                                        // Render merged unavailable block
                                        const startHour = item.startTime.hour();
                                        const startMinute = item.startTime.minute();
                                        const endHour = item.endTime.hour();
                                        const endMinute = item.endTime.minute();

                                        const minutesFromDayStart = (startHour - timeRange.startHour) * 60 + startMinute;
                                        const minutesFromDayEnd = (endHour - timeRange.startHour) * 60 + endMinute;
                                        const topPosition = (minutesFromDayStart / 60) * hourHeight;
                                        const blockHeight = ((minutesFromDayEnd - minutesFromDayStart) / 60) * hourHeight;

                                        return (
                                            <div
                                                key={`unavailable-merged-${groupIndex}`}
                                                className="mobile-slot-block mobile-slot-unavailable-merged"
                                                style={{
                                                    cursor: 'default',
                                                    position: 'absolute',
                                                    top: `${topPosition}px`,
                                                    height: `${blockHeight - 4}px`,
                                                    left: '2px',
                                                    right: '2px',
                                                    zIndex: 10
                                                }}
                                            >
                                                <div className="mobile-slot-content-merged">
                                                    <div className="mobile-slot-title">
                                                        Niedostępne
                                                    </div>
                                                    <div className="mobile-slot-duration">
                                                        {item.startTime.format('HH:mm')} - {item.endTime.format('HH:mm')}
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    } else {
                                        // Render single slot
                                        const { time: slotTime, endTime, type, bookedTraining, bookingInfo, isUnavailable, canCancel, actualDuration } = item;
                                        const slotStr = slotTime.format('YYYY-MM-DDTHH:mm:ss');
                                        const isSelected = selectedSlots.includes(slotStr);

                                        // Calculate position and height - use actual duration for each slot
                                        const slotStartHour = slotTime.hour();
                                        const slotStartMinute = slotTime.minute();
                                        const minutesFromDayStart = (slotStartHour - timeRange.startHour) * 60 + slotStartMinute;
                                        const topPosition = (minutesFromDayStart / 60) * hourHeight;

                                        // Use actual duration if available, otherwise use service duration
                                        const slotDuration = actualDuration || serviceDurationMinutes;
                                        const slotHeight = (slotDuration / 60) * hourHeight;

                                        const handleSlotClick = () => {
                                            if (type === 'free' && !isUnavailable) {
                                                if (schoolSettings?.allowMultipleBookings) {
                                                    onToggleSlot(slotTime);
                                                } else {
                                                    onBookSlot(slotTime);
                                                }
                                            } else if ((type === 'confirmed' || type === 'pending') && bookedTraining && canCancel) {
                                                handleDeclineTrainingConfirm(bookedTraining);
                                            }
                                        };

                                        const getSlotClass = () => {
                                            let baseClass = 'mobile-slot-block';
                                            switch (type) {
                                                case 'confirmed':
                                                    return `${baseClass} mobile-slot-confirmed`;
                                                case 'pending':
                                                    return `${baseClass} mobile-slot-pending`;
                                                case 'locked':
                                                    return `${baseClass} mobile-slot-locked`;
                                                case 'confirmed-other':
                                                    return `${baseClass} mobile-slot-confirmed-other`;
                                                case 'unavailable':
                                                    return `${baseClass} mobile-slot-unavailable`;
                                                case 'free':
                                                    return `${baseClass} ${isSelected ? 'mobile-slot-free-selected' : 'mobile-slot-free'}`;
                                                default:
                                                    return baseClass;
                                            }
                                        };

                                        const getSlotTitle = () => {
                                            switch (type) {
                                                case 'confirmed':
                                                    return 'Potwierdzony';
                                                case 'pending':
                                                    return 'Oczekuje';
                                                case 'locked':
                                                    return 'Zablokowane';
                                                case 'confirmed-other':
                                                    return 'Zajęte';
                                                case 'unavailable':
                                                    return 'Niedostępne';
                                                case 'free':
                                                    return isSelected ? 'Wybrane' : 'Wolny';
                                                default:
                                                    return '';
                                            }
                                        };

                                        const isClickable = (type === 'free' && !isUnavailable) ||
                                            ((type === 'confirmed' || type === 'pending') && bookedTraining && canCancel);
                                        const isCompact = slotHeight < 50;

                                        return (
                                            <div
                                                key={`${slotTime.format('HH:mm')}-${groupIndex}`}
                                                className={getSlotClass()}
                                                onClick={isClickable ? handleSlotClick : undefined}
                                                style={{
                                                    cursor: isClickable ? 'pointer' : 'default',
                                                    position: 'absolute',
                                                    top: `${topPosition}px`,
                                                    height: `${slotHeight - 4}px`,
                                                    left: '2px',
                                                    right: '2px',
                                                    zIndex: 10
                                                }}
                                            >
                                                <div className="mobile-slot-content">
                                                    <div className="mobile-slot-title">
                                                        {getSlotTitle()}
                                                    </div>
                                                    {!isCompact && (
                                                        <div className="mobile-slot-duration">
                                                            {slotTime.format('HH:mm')} - {endTime.format('HH:mm')}
                                                        </div>
                                                    )}
                                                    {!isCompact && bookingInfo && bookingInfo.bookingCount > 0 && (
                                                        <div className="mobile-slot-info">
                                                            Oczek: {bookingInfo.bookingCount}
                                                        </div>
                                                    )}
                                                </div>
                                                {bookedTraining && canCancel && (
                                                    <button
                                                        className="mobile-slot-cancel"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleDeclineTrainingConfirm(bookedTraining);
                                                        }}
                                                    >
                                                        ✕
                                                    </button>
                                                )}
                                                {type === 'free' && isSelected && (
                                                    <div className="mobile-slot-selected-indicator">✓</div>
                                                )}
                                            </div>
                                        );
                                    }
                                })}
                            </div>
                        </div>
                    </div>
                )}

                {timeSlots.length > 0 && (
                    <div className="mobile-swipe-hint">
                        Przesuń obszar z dniami w lewo/prawo aby przejść do innego tygodnia
                    </div>
                )}
            </div>
        );
    };

    return (
        <div className="mobile-calendar-view">
            {renderMobileWeekOverview()}
            {renderMobileDayView()}

            <DeleteConfirmationModal
                isOpen={deleteModalOpen}
                onClose={cancelDeleteTraining}
                onConfirm={confirmDeleteTraining}
                itemName={
                    trainingToDelete
                        ? `trening ${dayjs(trainingToDelete.startTime).format("HH:mm")} - ${dayjs(trainingToDelete.endTime).format("HH:mm")}`
                        : ""
                }
            />
        </div>
    );
};

export default MobileOwnerCalendar;